import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useCallback, useEffect } from 'react';
import { ModalWindow, Text, useLocalizeMessage } from 'libs.nucleus';
import { StepIndicator } from 'libs.react';
import { CloneDashboardSelect } from './clone_dashboard_select.component';
import { CreateDashboardForm } from './create_dashboard_form.component';
import { CreateDashboardOptionSelect } from './create_dashboard_option_select.component';
import { SelectCharts } from './select_charts.component';
import { useDashboards } from '../../hooks';
export const CreateDashboardModal = ({ onClose, onSuccess }) => {
    const translate = useLocalizeMessage();
    const { createDashboard, cloneDashboard, fetchDashboardById, fetchDashboards, dashboards, isLoading, generateUniqueCloneName, } = useDashboards();
    const [currentStep, setCurrentStep] = useState(1);
    const [creationOption, setCreationOption] = useState('create');
    // Clone flow: 2 steps (option → select dashboard)
    // Create flow: 3 steps (option → form → select charts)
    const totalSteps = creationOption === 'clone' ? 2 : 3;
    const isFirstStep = currentStep === 1;
    const isLastStep = currentStep === totalSteps;
    // Clone flow state
    const [selectedDashboardId, setSelectedDashboardId] = useState(null);
    const [isLoadingDashboards, setIsLoadingDashboards] = useState(false);
    // Create flow state
    const [formName, setFormName] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [formDatasetId, setFormDatasetId] = useState('');
    const [formDatasetKey, setFormDatasetKey] = useState('');
    const [isFormValid, setIsFormValid] = useState(false);
    const [selectedCharts, setSelectedCharts] = useState([]);
    // Fetch dashboards when entering clone flow step 2
    useEffect(() => {
        if (creationOption === 'clone' && currentStep === 2) {
            const loadDashboards = async () => {
                setIsLoadingDashboards(true);
                try {
                    await fetchDashboards();
                }
                catch {
                    // Error handled in hook
                }
                finally {
                    setIsLoadingDashboards(false);
                }
            };
            loadDashboards();
        }
    }, [creationOption, currentStep, fetchDashboards]);
    useEffect(() => {
        setSelectedDashboardId(null);
        setSelectedCharts([]);
        setFormName('');
        setFormDescription('');
        setFormDatasetId('');
        setFormDatasetKey('');
        setIsFormValid(false);
    }, [creationOption]);
    const handleFormChange = useCallback((name, description) => {
        setFormName(name);
        setFormDescription(description);
    }, []);
    const handleDatasetChange = useCallback((datasetId, datasetKey) => {
        setFormDatasetId(datasetId);
        setFormDatasetKey(datasetKey);
        setSelectedCharts([]);
    }, []);
    const handleValidationChange = useCallback((isValid) => {
        setIsFormValid(isValid);
    }, []);
    const handleCloseModal = useCallback(() => {
        setCreationOption('create');
        setSelectedDashboardId(null);
        setSelectedCharts([]);
        setFormName('');
        setFormDescription('');
        setFormDatasetId('');
        setFormDatasetKey('');
        setIsFormValid(false);
        setCurrentStep(1);
        onClose();
    }, [onClose]);
    const handleNext = useCallback(() => {
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        }
    }, [currentStep, totalSteps]);
    const handlePrevious = useCallback(() => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    }, [currentStep]);
    const handleSubmit = useCallback(async () => {
        try {
            let newDashboard;
            if (creationOption === 'clone' && selectedDashboardId) {
                const sourceDashboard = await fetchDashboardById(selectedDashboardId);
                if (!sourceDashboard) {
                    throw new Error('Source dashboard not found');
                }
                const uniqueName = await generateUniqueCloneName(sourceDashboard.name);
                newDashboard = await cloneDashboard(sourceDashboard, 'CLONE', { name: uniqueName });
            }
            else {
                newDashboard = await createDashboard({
                    name: formName.trim(),
                    description: formDescription.trim() || undefined,
                    cubeConfigId: formDatasetId,
                    cubeConfigKey: formDatasetKey,
                    charts: selectedCharts,
                });
            }
            onSuccess(newDashboard);
            handleCloseModal();
        }
        catch {
            // Error handled in hook
        }
    }, [
        creationOption,
        selectedDashboardId,
        cloneDashboard,
        createDashboard,
        fetchDashboardById,
        generateUniqueCloneName,
        formName,
        formDescription,
        formDatasetId,
        formDatasetKey,
        selectedCharts,
        onSuccess,
        handleCloseModal,
    ]);
    const canContinue = useMemo(() => {
        if (currentStep === 1) {
            return true;
        }
        if (currentStep === 2 && creationOption === 'create') {
            return isFormValid;
        }
        return false;
    }, [currentStep, creationOption, isFormValid]);
    const isSubmitDisabled = useMemo(() => {
        if (creationOption === 'clone') {
            return isLoading || !selectedDashboardId;
        }
        return isLoading || selectedCharts.length === 0;
    }, [creationOption, isLoading, selectedDashboardId, selectedCharts.length]);
    const continueButton = {
        label: translate('Continue'),
        onClick: handleNext,
        disabled: !canContinue,
    };
    const createButton = {
        label: translate('Create'),
        onClick: handleSubmit,
        disabled: isSubmitDisabled,
    };
    const footerSecondaryActionButtons = isFirstStep
        ? [
            {
                label: translate('Cancel'),
                onClick: handleCloseModal,
            },
        ]
        : [
            {
                label: translate('Cancel'),
                onClick: handleCloseModal,
            },
            {
                label: translate('Back'),
                onClick: handlePrevious,
            },
        ];
    const stepFooter = (_jsxs("div", { className: 'flex justify-center items-center', children: [_jsx(Text, { size: 'sm', className: 'px-6', children: translate('Step {current} of {total}', {
                    current: currentStep,
                    total: totalSteps,
                }) }), _jsx(StepIndicator, { currentStep: currentStep, totalSteps: totalSteps })] }));
    return (_jsx(ModalWindow, { title: translate('Create dashboard'), isOpen: true, closeWindow: handleCloseModal, footerPrimaryActionButton: isLastStep ? createButton : continueButton, footerSecondaryActionButtons: footerSecondaryActionButtons, footerCenterContent: stepFooter, width: 'full', children: _jsxs("div", { className: 'min-h-[400px] size-full', children: [currentStep === 1 && (_jsx(CreateDashboardOptionSelect, { selectedOption: creationOption, onOptionChange: setCreationOption })), currentStep === 2 && creationOption === 'create' && (_jsx(CreateDashboardForm, { name: formName, description: formDescription, selectedDatasetId: formDatasetId, onFormChange: handleFormChange, onDatasetChange: handleDatasetChange, onValidationChange: handleValidationChange })), currentStep === 2 && creationOption === 'clone' && (_jsx(CloneDashboardSelect, { dashboards: dashboards, isLoading: isLoadingDashboards, selectedDashboardId: selectedDashboardId, onDashboardSelect: setSelectedDashboardId })), currentStep === 3 && creationOption === 'create' && (_jsx(SelectCharts, { cubeConfigKey: formDatasetKey, selectedCharts: selectedCharts, onSelectionChange: setSelectedCharts }))] }) }));
};
